import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IOption, Select } from "@digital-services/ui";

import ErrorComponent from "@components/ErrorComponent";
import LoadingComponent from "@components/LoadingComponent";

import { OrganizationUser } from "@organization/models/OrganizationUser";
import { useGetOrganizationUsers } from "@organization/services/use-organization-users";

interface Props {
  label: string;
  selectedUserId?: string;
  disabled?: boolean;
  onUserChange: (user: OrganizationUser | undefined) => void;
}

export const OrganizationUserDropDown = (props: Props) => {
  const { label, selectedUserId, disabled = false, onUserChange } = props;

  const { t } = useTranslation(undefined, { keyPrefix: "organization.users" });

  const { organizationUsers, isLoading, error } = useGetOrganizationUsers();

  const [options, setOptions] = useState<IOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<IOption | null>(null);

  useEffect(() => {
    if (organizationUsers) {
      const sortedOptions = organizationUsers
        .map(({ id, displayName }) => ({
          value: id,
          label: displayName
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setOptions(sortedOptions);
    }
  }, [organizationUsers]);

  useEffect(() => {
    if (options.length) {
      if (selectedUserId) {
        const foundOption = options.find(
          option => option.value.toString() === selectedUserId
        );
        setSelectedOption(foundOption ?? null);
      }
    }
  }, [options, selectedUserId]);

  const handleChange = (option: IOption) => {
    setSelectedOption(option);

    const organizationUser = organizationUsers?.find(
      user => user.id === option.value
    );

    onUserChange(organizationUser);
  };

  if (isLoading) return <LoadingComponent message={t("load.message")} />;
  if (error) return <ErrorComponent message={t("load.error.message")} />;

  return (
    <Select
      data-testid="organization-user-dropdown"
      disabled={disabled}
      label={label}
      options={options}
      selected={selectedOption}
      onChange={handleChange}
      enableSearch={true}
    />
  );
};
